/**
 * Job Detail API Contract
 *
 * ts-rest contract definition for fetching and updating a single job's details.
 *
 * Endpoints:
 *   GET   /jobs/job-detail/:jobId
 *   PATCH /jobs/job-detail/:jobId
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { psrSchema } from '../work-orders/workOrderImport.contract';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

/**
 * Zod schema for a task within a job
 *
 * `status` is the raw `job_task.status` id (JOB_TASK scheme: 5/1=Not Started,
 * 9=In Progress, 10=Completed, 8=Abort). The mobile task-form submit path rolls
 * task completion up onto this column (see backend jobTaskForm/add.ts); the UI
 * maps the id to a badge.
 */
export const jobTaskSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  status: z.number().nullable(),
  orderNumber: z.number().nullable().optional(),
  isCategoryTask: z.boolean().optional(),
  // Effective mandatory state: per-job override → job category default → true.
  // Optional tasks do not block job completion on mobile.
  isMandatory: z.boolean().optional(),
  // True when this job overrides its category default (job_task.is_mandatory is
  // set). Lets the UI show "overridden" and offer a reset to the default.
  isMandatoryOverridden: z.boolean().optional(),
  // True when the job category marks this task mandatory. Such tasks are fixed
  // for every job in the category and can only be relaxed in Settings →
  // Category Tasks, so the per-job toggle is read-only.
  isMandatoryLocked: z.boolean().optional(),
});

/**
 * Zod schema for job milestones
 */
export const jobMilestonesSchema = z.record(z.string(), z.boolean());

/**
 * Works Lite permit lifecycle status values. Single source of truth for the
 * status literals — reference `PERMIT_STATUS.ACTIVE` etc. instead of bare
 * strings. The zod schema and the union type are both derived from it.
 */
export const PERMIT_STATUS = {
  ARRANGED: 'arranged',
  PENDING_START: 'pending_start',
  ACTIVE: 'active',
  // Extend request sent to SAP, awaiting confirmation. Extend/Stop are held and
  // the card shows a "waiting for extension approval" note until the extension
  // inbound lands and flips the permit back to ACTIVE.
  PENDING_EXTENSION: 'pending_extension',
  PENDING_STOP: 'pending_stop',
  STOPPED: 'stopped',
} as const;

export const permitLifecycleStatusSchema = z.enum([
  PERMIT_STATUS.ARRANGED,
  PERMIT_STATUS.PENDING_START,
  PERMIT_STATUS.ACTIVE,
  PERMIT_STATUS.PENDING_EXTENSION,
  PERMIT_STATUS.PENDING_STOP,
  PERMIT_STATUS.STOPPED,
]);

export type PermitLifecycleStatus = z.infer<typeof permitLifecycleStatusSchema>;

/**
 * SAP order types that surface the Works Lite permit UI. ZRP1 = permit
 * mandatory, ZRC2 = permit may be required (user opts in via Request Permit).
 * The wire type for `order_type` is kept as `string` — the backend forwards
 * whatever SAP sends — but consumers should use {@link isPermitOrderType} to
 * narrow safely.
 */
export const PERMIT_ORDER_TYPES = ['ZRP1', 'ZRC2'] as const;
export type PermitOrderType = (typeof PERMIT_ORDER_TYPES)[number];
export function isPermitOrderType(value: string | null | undefined): value is PermitOrderType {
  return value != null && (PERMIT_ORDER_TYPES as readonly string[]).includes(value);
}

/**
 * Permit category resolved by CORE (source of truth) after the SAP DISP
 * confirmation, delivered via a work_order.permit_update event. Drives the
 * permit workflow and engineer status gates. `PENDING` mirrors CORE's
 * unresolved state; null on the job means no category has been received yet.
 *
 * Single source of truth for the category literals — reference
 * `PERMIT_CATEGORY.PLANNED` etc. instead of bare strings. The zod schema and
 * the union type are both derived from it.
 */
export const PERMIT_CATEGORY = {
  PLANNED: 'planned',
  IMMEDIATE: 'immediate',
  NONE: 'none',
  PENDING: 'pending',
} as const;

export const permitCategorySchema = z.enum([
  PERMIT_CATEGORY.PLANNED,
  PERMIT_CATEGORY.IMMEDIATE,
  PERMIT_CATEGORY.NONE,
  PERMIT_CATEGORY.PENDING,
]);
export type PermitCategory = z.infer<typeof permitCategorySchema>;

/**
 * Map a permit category as it might arrive on the wire to its canonical code.
 *
 * CORE sends the lowercase codes today, but the integration doc
 * ("work_order.permit_update — Permit Category") documents the values as the
 * display labels `Planned` / `Immediate` / `No Permit`, and CORE's own activity
 * log uses exactly those labels (PERMIT_CATEGORY_LABEL in
 * handle-disp-confirmation-ack.ts). A label-form payload would fail the inbound
 * enum outright and the category would never land — the same silent-integration
 * break as CWL-161 / CWL-301, but with a 400 instead of a no-op. Accepting both
 * spellings costs nothing and removes the trip hazard.
 *
 * Returns null for anything unrecognised: the caller should reject rather than
 * guess, so a genuinely new category surfaces as a failed message instead of
 * being silently dropped.
 */
export function normalizePermitCategory(value: unknown): PermitCategory | null {
  if (typeof value !== 'string') return null;
  switch (
    value
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, ' ')
  ) {
    case 'planned':
    case 'planned notice':
      return PERMIT_CATEGORY.PLANNED;
    case 'immediate':
    case 'immediate notice':
      return PERMIT_CATEGORY.IMMEDIATE;
    case 'none':
    case 'no permit':
      return PERMIT_CATEGORY.NONE;
    case 'pending':
      return PERMIT_CATEGORY.PENDING;
    default:
      return null;
  }
}

/**
 * The categories that drive an actual permit workflow (Start / Extend / Stop).
 * `none` has no workflow and `pending` is unresolved, so both are excluded.
 */
export type ActionablePermitCategory = Extract<PermitCategory, 'planned' | 'immediate'>;

/**
 * ZRP1 with an `immediate` permit category is a special case: the permit may
 * arrive late or never, so — unlike a `planned` ZRP1 or any ZRC2 — the job must
 * not be held on the permit at all. When this is true every permit gate is
 * skipped and the work order progresses as if the category were a resolved
 * `none`.
 *
 * Scope history:
 *  - WRK-5786 introduced this for EN ROUTE (the engineer must be able to reach
 *    site without the permit).
 *  - WRK-5885 narrowed it to En Route only, so Technical Completion still
 *    required the permit to be stopped. That stranded any ZRP1 + immediate job
 *    whose permit never arrived — there was no permit to stop — which WRK-9999
 *    then partly papered over with a forms-complete override.
 *  - CWL-125 restores the full ungate: "For ZRP1 work order type when permit
 *    category on WL is set to IMMEDIATE the WO should be able to carry on as
 *    normal without pending information message." An immediate permit that
 *    never lands must not block completion, and it must not surface a
 *    pending-permit message either.
 *
 * If an immediate permit DOES arrive, its Start/Extend/Stop actions stay
 * on-site-gated (assertPermitActionAllowed) — this predicate governs the work
 * order's own progression, not the permit's own lifecycle.
 *
 * Deliberately narrow: ZRC2 (including its `none` unblock path) and `planned`
 * ZRP1 keep the full permit gating.
 *
 * Shared by the backend gate (permitGate.ts), the Works Lite UI (StatusBar.tsx,
 * PermitCard.tsx) and mobile so the rule can't drift between layers.
 */
export function permitProgressionUngated(
  orderType: string | null | undefined,
  category: PermitCategory | null | undefined,
): boolean {
  return orderType === 'ZRP1' && category === PERMIT_CATEGORY.IMMEDIATE;
}

export const jobPermitSchema = z.object({
  id: z.number(),
  permit_reference: z.string(),
  status: permitLifecycleStatusSchema,
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
});

export type JobPermit = z.infer<typeof jobPermitSchema>;

/**
 * Zod schema for a single job detail response
 */
export const jobDetailSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  address: z.string().nullable(),
  town: z.string().nullable(),
  county: z.string().nullable(),
  pincode: z.string().nullable(),
  coordinates: z.string().nullable(),
  // Legacy free-text column, kept verbatim as CORE sent it. Do not use for
  // display or editing — `priority_id`/`priority_name` are canonical (SC-6369).
  priority: z.string().nullable(),
  // `job_priority.id` — what the priority selector binds to.
  priority_id: z.number().nullable(),
  priority_name: z.string().nullable(),
  status: z.number().nullable(),
  job_status_name: z.string().nullable(),
  work_order_status: z.string().nullable(),
  work_order_status_id: z.number().nullable(),
  permit_required: z.boolean(),
  reinstatement_required: z.boolean(),
  permit_status_id: z.number().nullable(),
  permit_status_name: z.string().nullable(),
  job_category_name: z.string().nullable(),
  client_name: z.string().nullable(),
  contract_name: z.string().nullable(),
  assigned_to: z.number().nullable(),
  assigned_to_name: z.string().nullable(),
  team_id: z.number().nullable(),
  team_name: z.string().nullable(),
  // Actual scheduled datetime (schedule.start_date_time). Null until the job is
  // scheduled by a planner — distinct from start_date, which CORE/SAP always
  // sets (WRK-5663).
  scheduled_date_time: z.string().nullable(),
  /**
   * True when a live `schedule` row exists for the job. Both
   * schedule.start_date_time and schedule.team_id are nullable, so neither
   * scheduled_date_time nor team_id is a reliable "is assigned" test (WRK-5792).
   */
  is_scheduled: z.boolean(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  req_completion_date: z.string().nullable(),
  created_time: z.string().nullable(),
  created_by: z.string().nullable(),
  notes: z.string().nullable(),
  external_id: z.string().nullable(),
  client_ref_no: z.string().nullable(),
  application_reference: z.string().nullable(),
  tags: z.unknown().nullable(),
  validated: z.boolean(),
  duration: z.number().nullable(),
  duration_unit: z.enum(['minutes', 'hours', 'days']).nullable(),
  mir_pending_direction: z.enum(['inbound', 'outbound']).nullable(),
  psr: psrSchema.nullable().optional(),
  order_type: z.string().nullable(),
  permit: jobPermitSchema.nullable(),
  permit_request_sent_at: z.string().nullable(),
  permit_category: permitCategorySchema.nullable(),
  // True when the job has a mandatory task that is either not yet resolved
  // (status neither Completed nor Abort) or Completed with no submitted form.
  // Mirrors findOutstandingMandatoryWork.length > 0 (mandatoryWork.ts) — the
  // same check permitGate.ts's technical-completion gate now runs server-side
  // (WRK-9999) — so the web UI's own permit gate can apply the identical
  // forms-complete override instead of carrying a second, divergent copy.
  mandatory_work_outstanding: z.boolean(),
  // True once the engineer has reached site — the work order is at 'On Site' or
  // has moved beyond it. Mirrors hasReachedOnSite (permitGate.ts), the same rule
  // the Start/Extend/Stop API gate applies, so PermitCard can enable the buttons
  // exactly when the API would accept them (CWL-118). Ranking the lifecycle
  // rather than matching 'On Site' by name matters because finishing the
  // mandatory forms auto-promotes the work order to 'Engineering Completed'
  // while the engineer is still on site.
  engineer_on_site: z.boolean(),
  tasks: z.array(jobTaskSchema),
  milestones: jobMilestonesSchema,
});

export const jobDetailResponseSchema = jobDetailSchema;

/**
 * Request body for updating a job's detail.
 * `duration` is expressed in whole hours (min 1) and is persisted by
 * computing `req_completion_date = start_date + duration hours`.
 */
export const updateJobDetailBodySchema = z.object({
  duration: z.number().int().min(1).optional(),
  jobCategoryId: z.number().int().positive().optional(),
  /**
   * `job_priority.id` — the canonical priority. Only `priority_id` is written;
   * the legacy free-text `job.priority` column is left untouched (SC-6369).
   * Must reference an ACTIVE row, which includes `Unknown` (id 5) — a real,
   * selectable priority meaning "not specified", not an absent value.
   */
  priorityId: z.number().int().positive().optional(),
  address: z.string().max(500).optional(),
  town: z.string().max(100).optional(),
  county: z.string().max(100).optional(),
  pincode: z.string().max(50).optional(),
  coordinates: z.string().max(100).nullable().optional(),
  permitRequired: z.boolean().optional(),
  reinstatementRequired: z.boolean().optional(),
  permitStatusId: z.number().int().positive().nullable().optional(),
});

export const addressSearchResultSchema = z.object({
  id: z.string(),
  type: z.string(),
  text: z.string(),
  description: z.string(),
});

export const addressDetailSchema = z.object({
  line1: z.string(),
  line2: z.string(),
  city: z.string(),
  county: z.string(),
  postcode: z.string(),
  country: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

export type AddressSearchResult = z.infer<typeof addressSearchResultSchema>;
export type AddressDetail = z.infer<typeof addressDetailSchema>;

export const addJobTaskBodySchema = z.object({
  taskTypeId: z.coerce.number().int().positive(),
  orderNumber: z.coerce.number().int().positive().optional(),
});

export const removeJobTaskResponseSchema = z.object({
  message: z.string(),
});

/**
 * Body for overriding a task's mandatory state on one job. `null` clears the
 * override so the task inherits its job category default again.
 */
export const setJobTaskMandatoryBodySchema = z.object({
  isMandatory: z.boolean().nullable(),
});

export const setJobTaskMandatoryResponseSchema = z.object({
  id: z.number(),
  isMandatory: z.boolean(),
  isMandatoryOverridden: z.boolean(),
  isMandatoryLocked: z.boolean(),
});

export type JobDetail = z.infer<typeof jobDetailSchema>;
export type JobDetailTask = z.infer<typeof jobTaskSchema>;
export type JobDetailMilestones = z.infer<typeof jobMilestonesSchema>;
export type UpdateJobDetailBody = z.infer<typeof updateJobDetailBodySchema>;
export type AddJobTaskBody = z.infer<typeof addJobTaskBodySchema>;
export type SetJobTaskMandatoryBody = z.infer<typeof setJobTaskMandatoryBodySchema>;
export type SetJobTaskMandatoryResponse = z.infer<typeof setJobTaskMandatoryResponseSchema>;

/**
 * Job Detail API Contract
 *
 * Base path: /jobs/job-detail  (mounted in apps/backend/src/domains/works-lite/jobs/routes.ts)
 */
export const jobDetailContract = c.router({
  /**
   * Get details for a single job
   *
   * GET /jobs/job-detail/:jobId
   */
  getJobDetail: {
    method: 'GET',
    path: '/:jobId',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    responses: {
      200: jobDetailResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get details for a single job',
    description: 'Returns the full job detail with tasks, milestones, and resolved lookup names.',
  },

  /**
   * Update editable fields on a job's detail.
   *
   * PATCH /jobs/job-detail/:jobId
   *
   * Supports duration, job category, priority, address and permit fields.
   * Returns the same shape as getJobDetail.
   */
  updateJobDetail: {
    method: 'PATCH',
    path: '/:jobId',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: updateJobDetailBodySchema,
    responses: {
      200: jobDetailResponseSchema,
      400: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update editable fields on a job detail',
    description:
      'Updates editable fields on a job: duration (hours, persisted by updating ' +
      'req_completion_date relative to start_date), job category, priority, ' +
      'address and permit fields. Only the keys provided are written.',
  },

  addJobTask: {
    method: 'POST',
    path: '/:jobId/tasks',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: addJobTaskBodySchema,
    responses: {
      201: jobTaskSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Add a task to a job',
    description: 'Creates a job_task record linking the specified task type to the job.',
  },

  setJobTaskMandatory: {
    method: 'PATCH',
    path: '/:jobId/tasks/:taskId/mandatory',
    pathParams: z.object({
      jobId: z.coerce.number(),
      taskId: z.coerce.number(),
    }),
    body: setJobTaskMandatoryBodySchema,
    responses: {
      200: setJobTaskMandatoryResponseSchema,
      400: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Override whether a task is mandatory on this job',
    description:
      'Sets job_task.is_mandatory for a single job, overriding the job category default. ' +
      'Send isMandatory: null to clear the override and inherit the category default again. ' +
      'Only tasks their job category marks optional can be overridden — a category-mandatory ' +
      'task returns 400 MANDATORY_LOCKED and must be changed in Settings → Category Tasks.',
  },

  removeJobTask: {
    method: 'DELETE',
    path: '/:jobId/tasks/:taskId',
    pathParams: z.object({
      jobId: z.coerce.number(),
      taskId: z.coerce.number(),
    }),
    body: z.any().optional(),
    responses: {
      200: removeJobTaskResponseSchema,
      400: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Remove a task from a job',
    description:
      'Soft-deletes a job_task record. Cannot remove tasks that come from the job category assignment.',
  },

  getMilestoneHistory: {
    method: 'GET',
    path: '/:jobId/milestone-history',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    responses: {
      200: z.object({
        events: z.array(
          z.object({
            previous_value: z.string().nullable(),
            new_value: z.string().nullable(),
          }),
        ),
      }),
      500: errorResponseSchema,
    },
    summary: 'Get milestone history for a job',
    description:
      'Returns a record of milestone status names to boolean, derived from audit_trail status_change events.',
  },

  addressSearch: {
    method: 'GET',
    path: '/address/search',
    query: z.object({
      text: z.string(),
      container: z.string().optional(),
    }),
    responses: {
      200: z.array(addressSearchResultSchema),
      500: errorResponseSchema,
    },
    summary: 'Search for addresses via Loqate',
  },

  addressDetail: {
    method: 'GET',
    path: '/address/detail',
    query: z.object({
      id: z.string(),
    }),
    responses: {
      200: addressDetailSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get full address detail from Loqate by ID',
  },
});
